import { useEffect, useRef, useCallback } from 'react';
import supabase from '../utils/supabase';

/**
 * Custom hook for managing real-time subscriptions
 * Prevents duplicate subscriptions and handles cleanup properly
 * Implements exponential backoff for reconnection attempts
 */
export const useRealtimeSubscription = (
  channel,
  events,
  onUpdate,
  dependencies = []
) => {
  const subscriptionRef = useRef(null);
  const isSubscribedRef = useRef(false);
  const isActiveMountRef = useRef(true);
  const reconnectTimeoutRef = useRef(null);
  const reconnectCountRef = useRef(0);
  const maxReconnectAttempts = 5;

  // Cleanup previous subscription safely
  const cleanupSubscription = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (subscriptionRef.current) {
      try {
        // Unsubscribe first, then remove channel
        subscriptionRef.current.unsubscribe?.();
        supabase.removeChannel(subscriptionRef.current);
      } catch (err) {
        console.error(`Error cleaning up subscription ${channel}:`, err.message);
      }
      subscriptionRef.current = null;
    }
    isSubscribedRef.current = false;
  }, [channel]);

  // Reconnect with exponential backoff
  const attemptReconnect = useCallback(
    (currentChannel, currentEvents, currentOnUpdate) => {
      if (!isActiveMountRef.current || reconnectCountRef.current >= maxReconnectAttempts) {
        console.warn(
          `Stopped reconnection attempts for ${currentChannel} (attempts: ${reconnectCountRef.current})`
        );
        return;
      }

      const backoffMs = Math.min(1000 * Math.pow(2, reconnectCountRef.current), 30000);
      console.log(
        `Scheduling reconnection for ${currentChannel} in ${backoffMs}ms (attempt ${reconnectCountRef.current + 1})`
      );

      reconnectTimeoutRef.current = setTimeout(() => {
        if (isActiveMountRef.current) {
          reconnectCountRef.current += 1;
          setupSubscription(currentChannel, currentEvents, currentOnUpdate);
        }
      }, backoffMs);
    },
    []
  );

  // Setup subscription with error handling
  const setupSubscription = useCallback(
    (currentChannel, currentEvents, currentOnUpdate) => {
      if (!currentChannel || !currentEvents || !currentOnUpdate || !isActiveMountRef.current) {
        return;
      }

      // Clean up any existing subscription first
      cleanupSubscription();

      console.log(`Setting up realtime subscription: ${currentChannel}`);

      try {
        const subscription = supabase.channel(currentChannel, {
          config: {
            // Broadcast configuration - ensure proper timeout
            broadcast: { ack: true },
          },
        });

        let payloadCount = 0;

        // Subscribe to all specified events
        currentEvents.forEach(({ event, schema, table, filter }) => {
          subscription.on(
            'postgres_changes',
            { event, schema, table, ...(filter && { filter }) },
            async (payload) => {
              if (!isActiveMountRef.current || !isSubscribedRef.current) return;

              payloadCount++;
              console.log(
                `[${payloadCount}] Realtime update on ${currentChannel}:`,
                `${event} on ${table}`,
                payload.new || payload.old ? '✓' : '✗'
              );

              try {
                // Small delay to ensure DB consistency
                await new Promise(resolve => setTimeout(resolve, 100));
                await currentOnUpdate(payload);
              } catch (err) {
                console.error(`Error processing realtime update on ${currentChannel}:`, err);
              }
            }
          );
        });

        // Subscribe and handle status changes
        const unsubscribe = subscription.subscribe((status, err) => {
          if (!isActiveMountRef.current) return;

          console.log(`[${currentChannel}] Subscription status: ${status}`, err || '');

          if (status === 'SUBSCRIBED') {
            isSubscribedRef.current = true;
            reconnectCountRef.current = 0; // Reset reconnect counter on success
            console.log(`✓ Subscription active: ${currentChannel}`);
          } else if (status === 'CLOSED') {
            isSubscribedRef.current = false;
            console.warn(`⚠ Subscription closed: ${currentChannel}`);
            attemptReconnect(currentChannel, currentEvents, currentOnUpdate);
          } else if (status === 'CHANNEL_ERROR') {
            isSubscribedRef.current = false;
            console.error(`✗ Subscription error: ${currentChannel}`, err?.message || '');
            attemptReconnect(currentChannel, currentEvents, currentOnUpdate);
          }
        });

        subscriptionRef.current = subscription;
      } catch (err) {
        console.error(`Failed to setup subscription ${currentChannel}:`, err.message);
        attemptReconnect(currentChannel, currentEvents, currentOnUpdate);
      }
    },
    [cleanupSubscription, attemptReconnect]
  );

  // Main effect: setup and cleanup subscription
  useEffect(() => {
    isActiveMountRef.current = true;
    reconnectCountRef.current = 0;

    if (!channel || !events || !onUpdate || events.length === 0) {
      cleanupSubscription();
      return;
    }

    setupSubscription(channel, events, onUpdate);

    // Cleanup on unmount
    return () => {
      isActiveMountRef.current = false;
      cleanupSubscription();
    };
  }, [channel, onUpdate]); // Only depend on channel and onUpdate, not events array

  return {
    isSubscribed: isSubscribedRef.current,
    unsubscribe: cleanupSubscription,
    reconnectAttempts: reconnectCountRef.current,
  };
};
